import { useState } from "react";
import { useSearchParams } from "react-router";
import type { AdminAuditEventSummary } from "../../../lib/api";
import { consoleApi } from "../../../lib/admin-console-api";
import { useAppPreferences } from "../../../lib/preferences";
import { actionLabel, EventRow } from "../activity";
import { errorMessage, LoadState, PageHeader, Section, useLoad } from "../ui";

const ENTITY_TYPES = [
  "provider",
  "venue",
  "user",
  "venue_review",
  "ugc_review_report",
  "account_deletion_request",
  "assistance",
  "receipt",
  "tournament",
];

const ACTIONS = [
  "provider_status_updated",
  "venue_status_updated",
  "venue_taken_down",
  "review_status_updated",
  "review_removed",
  "ugc_report_resolved",
  "account_deletion_approved",
  "account_deactivated",
  "admin_assistance_started",
  "admin_assistance_write",
  "commission_rule_created",
  "settlement_statement_issued",
  "settlement_payment_recorded",
  "platform_user_access_updated",
];

export function AuditPage() {
  const { t } = useAppPreferences();
  const [params, setParams] = useSearchParams();
  const filters = {
    entity_type: params.get("entity_type") ?? "",
    action: params.get("action") ?? "",
    from_date: params.get("from") ? `${params.get("from")}T00:00:00Z` : "",
    to_date: params.get("to") ? `${params.get("to")}T23:59:59Z` : "",
  };
  const key = params.toString();
  const [older, setOlder] = useState<AdminAuditEventSummary[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [moreError, setMoreError] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const page = useLoad(async () => {
    const result = await consoleApi.listAudit({ ...filters, limit: 50 });
    setOlder([]);
    setCursor(result.next_cursor);
    return result.events;
  }, [key]);
  const events = [...(page.data ?? []), ...older];

  const setFilter = (name: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(name, value);
    else next.delete(name);
    setParams(next);
  };

  const loadMore = async () => {
    if (!cursor) return;
    setLoadingMore(true);
    setMoreError(null);
    try {
      const result = await consoleApi.listAudit({ ...filters, limit: 50, cursor });
      setOlder((current) => [...current, ...result.events]);
      setCursor(result.next_cursor);
    } catch (error) {
      setMoreError(errorMessage(error, t("Older events could not be loaded.")));
    } finally {
      setLoadingMore(false);
    }
  };

  return (
    <div className="console-page">
      <PageHeader description={t("Every staff decision and sensitive venue change, newest first.")} kicker={t("Platform")} title={t("Audit log")} />
      <div className="console-toolbar">
        <label className="ops-field">
          <span className="ops-label">{t("Record type")}</span>
          <select className="ops-select" onChange={(event) => setFilter("entity_type", event.target.value)} value={filters.entity_type}>
            <option value="">{t("All")}</option>
            {ENTITY_TYPES.map((type) => <option key={type} value={type}>{t(type.replaceAll("_", " "))}</option>)}
          </select>
        </label>
        <label className="ops-field">
          <span className="ops-label">{t("Action")}</span>
          <select className="ops-select" onChange={(event) => setFilter("action", event.target.value)} value={filters.action}>
            <option value="">{t("All")}</option>
            {ACTIONS.map((action) => <option key={action} value={action}>{actionLabel(action, t)}</option>)}
          </select>
        </label>
        <label className="ops-field">
          <span className="ops-label">{t("From")}</span>
          <input className="ops-input" onChange={(event) => setFilter("from", event.target.value)} type="date" value={params.get("from") ?? ""} />
        </label>
        <label className="ops-field">
          <span className="ops-label">{t("To")}</span>
          <input className="ops-input" onChange={(event) => setFilter("to", event.target.value)} type="date" value={params.get("to") ?? ""} />
        </label>
      </div>
      <Section title={t("Events")}>
        <LoadState empty={t("No events match these filters.")} error={page.error} isEmpty={events.length === 0} loading={page.loading} onRetry={() => void page.reload()}>
          <ol className="console-timeline">
            {events.map((event) => <EventRow event={event} key={event.id} showEntity />)}
          </ol>
          {moreError ? <p className="console-error" role="alert">{moreError}</p> : null}
          {cursor ? (
            <button className="ops-button" data-size="sm" data-variant="secondary" disabled={loadingMore} onClick={() => void loadMore()} type="button">
              {loadingMore ? t("Loading...") : t("Load older events")}
            </button>
          ) : null}
        </LoadState>
      </Section>
    </div>
  );
}
